import type { WorksheetFormEntity } from '../../../worksheets/forms/entities';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { toWorksheetFormEntity } from '../../../worksheets/forms/helpers';

/** Parameters for fetching all worksheet submissions for a case in parallel. */
export type FetchAllSubmissionsParams = {
    /** The case number the submissions belong to. */
    caseNumber: string;

    /** The submission IDs to fetch. */
    submissionIds: string[];
};

/**
 * Fetches all worksheet submissions for a case in parallel.
 * Each submission is fetched independently; failed fetches are silently excluded from the result.
 */
export class FetchAllSubmissions extends StsBackendOperation {
    /**
     * Fetch all worksheet submissions for the given case in parallel.
     *
     * @param params - the case number and array of submission IDs to fetch
     * @returns a map keyed by submission ID, containing only the submissions that were fetched successfully
     */
    async exec(params: FetchAllSubmissionsParams): Promise<Map<string, WorksheetFormEntity>> {
        const { caseNumber, submissionIds } = params;

        // Fetch all submissions in parallel
        const results = await Promise.all(
            submissionIds.map(async (submissionId) => {
                const response = await this.client.retrieveWorksheetSubmission({ caseNumber, submissionId });
                return { submissionId, response };
            })
        );

        // Collect successful responses into a map
        const submissionMap = new Map<string, WorksheetFormEntity>();

        for (const { submissionId, response } of results) {
            if (response.success) {
                submissionMap.set(submissionId, toWorksheetFormEntity(response.data));
            }
        }

        return submissionMap;
    }
}
